/**
 * Rules for the self-registration email-domain list (roles 1–3).
 *
 * A domain on the list links new registrations to its group automatically
 * (POST /users/registration-domain-check answers needs_organization: false),
 * so it must identify one organization's staff. Public mailbox providers
 * like gmail.com never can, so they're refused.
 */

export const PUBLIC_EMAIL_DOMAINS = [
  '126.com',
  '163.com',
  'aol.com',
  'att.net',
  'bellsouth.net',
  'charter.net',
  'comcast.net',
  'cox.net',
  'earthlink.net',
  'fastmail.com',
  'frontier.com',
  'gmail.com',
  'gmx.com',
  'gmx.net',
  'googlemail.com',
  'hey.com',
  'hotmail.com',
  'icloud.com',
  'juno.com',
  'live.com',
  'mac.com',
  'mail.com',
  'me.com',
  'msn.com',
  'optonline.net',
  'outlook.com',
  'pm.me',
  'proton.me',
  'protonmail.com',
  'qq.com',
  'rediffmail.com',
  'rocketmail.com',
  'sbcglobal.net',
  'tutanota.com',
  'verizon.net',
  'windstream.net',
  'yahoo.com',
  'yandex.com',
  'yandex.ru',
  'ymail.com',
  'zoho.com',
  'zohomail.com'
];

// Country variants of the big providers: yahoo.co.in, hotmail.co.uk, …
const PUBLIC_PROVIDER_FAMILY =
  /^(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|msn|aol|rediffmail)\.[a-z]{2,3}(\.[a-z]{2})?$/;

const DOMAIN_PATTERN =
  /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/**
 * What an admin types or pastes ("@Bamsi.org", "jane@bamsi.org",
 * "https://bamsi.org/team") as a bare, lower-case domain ("bamsi.org").
 */
export const normalizeDomain = value => {
  let text = String(value || '')
    .trim()
    .toLowerCase();
  text = text.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  text = text.replace(/[/?#].*$/, '');
  if (text.includes('@')) {
    text = text.split('@').pop();
  }
  return text.replace(/\.+$/, '');
};

export const isPublicEmailDomain = domain =>
  PUBLIC_EMAIL_DOMAINS.includes(domain) || PUBLIC_PROVIDER_FAMILY.test(domain);

/**
 * Why this (normalized) domain can't be added, or '' when it can.
 * `existing` are the domains already on the list ({ domain, group_name }).
 */
export const validateDomain = (domain, existing = []) => {
  if (!domain) {
    return 'Enter the email domain, for example bamsi.org.';
  }
  if (!DOMAIN_PATTERN.test(domain)) {
    return "That doesn't look like an email domain. Use something like bamsi.org.";
  }
  if (isPublicEmailDomain(domain)) {
    return `${domain} is a public email provider. Anyone can get an address there, so it can't identify a group's staff.`;
  }
  const taken = existing.find(row => row.domain === domain);
  if (taken) {
    return `${domain} is already linked to ${taken.group_name || 'a group'}.`;
  }
  return '';
};

/** "www.bamsi.org" → "bamsi.org": email addresses almost never use www. */
export const suggestWithoutWww = domain =>
  domain.startsWith('www.') && domain.length > 4 ? domain.slice(4) : '';
